import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://acbuy.monster';

const shoppingGuideSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'ACBuy Spreadsheet Shopping Guide: How to Buy Smart in 2026',
  description: 'Step-by-step ACBuy Spreadsheet shopping guide covering how to browse categories, compare prices, read seller ratings, and save money using the ACBuy database in 2026.',
  image: 'https://readdy.ai/api/search-image?query=Clean%20organized%20modern%20shopping%20workspace%20with%20laptop%20tablet%20and%20smartphone%20displaying%20ecommerce%20interfaces%2C%20warm%20cream%20beige%20background%2C%20soft%20natural%20window%20light%2C%20minimalist%20desk%20setup%20with%20plants%2C%20editorial%20photography%20style%2C%20calm%20productive%20atmosphere%2C%20harmonious%20neutral%20composition%2C%20online%20shopping%20concept%2C%20no%20text%20on%20screens&width=1200&height=514&seq=guide-hero-2026-k1&orientation=landscape',
  datePublished: '2026-06-01',
  dateModified: '2026-06-24',
  author: {
    '@type': 'Organization',
    name: 'ACBuy Spreadsheet',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'ACBuy Spreadsheet',
    url: siteUrl,
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/shopping-guide`,
  },
};

export default function ShoppingGuidePage() {
  return (
    <>
      <JsonLd data={shoppingGuideSchema} />
      <Navbar />
      <main>
        <article className="pt-24 pb-16 md:pb-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900">Shopping Guide</span>
              </nav>

              <header className="mb-10">
                <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full bg-accent-100 text-accent-700 mb-4">
                  Step-by-Step Guide
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                  ACBuy Spreadsheet Shopping Guide: How to Buy Smart in 2026
                </h1>
                <p className="text-foreground-500 text-sm">
                  Last updated: June 2026 · 10 min read
                </p>
              </header>

              <div className="w-full aspect-[21/9] rounded-xl overflow-hidden mb-10 bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20organized%20modern%20shopping%20workspace%20with%20laptop%20tablet%20and%20smartphone%20displaying%20ecommerce%20interfaces%2C%20warm%20cream%20beige%20background%2C%20soft%20natural%20window%20light%2C%20minimalist%20desk%20setup%20with%20plants%2C%20editorial%20photography%20style%2C%20calm%20productive%20atmosphere%2C%20harmonious%20neutral%20composition%2C%20online%20shopping%20concept%2C%20no%20text%20on%20screens&width=1200&height=514&seq=guide-hero-2026-k1&orientation=landscape"
                  alt="ACBuy Spreadsheet shopping guide for smart buying in 2026"
                  title="ACBuy Spreadsheet Shopping Guide 2026"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose-custom text-foreground-700 leading-relaxed space-y-6">
                <p className="text-lg text-foreground-800 leading-relaxed">
                  Welcome to the official <strong>ACBuy Spreadsheet shopping guide</strong>. Whether you are
                  a first-time buyer or an experienced cross-border shopper, this step-by-step tutorial will
                  walk you through everything you need to know to shop confidently, save money, and find the
                  best products using the <strong>ACBuy spreadsheet database</strong>.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                  Step 1: Understand What the ACBuy Spreadsheet Is
                </h2>
                <p>
                  The <strong>ACBuy spreadsheet</strong> is a curated product comparison database that
                  aggregates listings from verified sellers across 14 major categories. Unlike traditional
                  shopping websites, the ACBuy database focuses on transparency — every listing includes
                  verified prices, real buyer ratings, shipping estimates, and seller trust scores. This
                  means you can compare products objectively without relying on marketing hype.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                  Step 2: Browse Categories That Match Your Needs
                </h2>
                <p>
                  The <strong>ACBuy item list</strong> is organized into 14 intuitive categories. Start
                  by identifying which category matches what you are looking for. The most popular
                  categories — Shoes, Hoodies and Sweaters, and T-Shirts — are excellent starting points
                  for new users because they feature the most thoroughly vetted products with the highest
                  number of buyer reviews.
                </p>

                <div className="bg-background-100 border border-background-200 rounded-xl p-6 my-8">
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                    Complete Category Directory
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { name: 'Shoes', icon: 'ri-footprint-line', rating: '4.8' },
                      { name: 'Hoodies / Sweaters', icon: 'ri-t-shirt-line', rating: '4.7' },
                      { name: 'T-Shirts', icon: 'ri-shirt-line', rating: '4.6' },
                      { name: 'Jackets', icon: 'ri-vip-crown-line', rating: '4.6' },
                      { name: 'Pants / Shorts', icon: 'ri-pantone-line', rating: '4.5' },
                      { name: 'Headwear', icon: 'ri-user-smile-line', rating: '4.4' },
                      { name: 'Sets', icon: 'ri-stack-line', rating: '4.7' },
                      { name: 'Underwear', icon: 'ri-checkbox-blank-line', rating: '4.3' },
                      { name: 'Jersey', icon: 'ri-medal-line', rating: '4.5' },
                      { name: 'Accessories', icon: 'ri-glasses-line', rating: '4.6' },
                      { name: 'Bags', icon: 'ri-briefcase-line', rating: '4.6' },
                      { name: 'Belts', icon: 'ri-loop-left-line', rating: '4.4' },
                      { name: 'Perfumes', icon: 'ri-drop-line', rating: '4.3' },
                      { name: 'Other Stuff', icon: 'ri-more-line', rating: '4.2' },
                    ].map((cat) => (
                      <div key={cat.name} className="flex items-center gap-3 bg-background-50 rounded-lg p-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-md flex-shrink-0">
                          <i className={`${cat.icon} text-sm text-primary-600`}></i>
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-foreground-900 block">{cat.name}</span>
                          <span className="text-xs text-foreground-500">Rating: {cat.rating}/5</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                  Step 3: Read Seller Ratings and Reviews Carefully
                </h2>
                <p>
                  The <strong>ACBuy spreadsheet comparison</strong> system assigns every seller a trust
                  score based on transaction history, buyer feedback, and delivery reliability. Before
                  clicking through to any product, check the seller&apos;s rating. We recommend sticking
                  with sellers rated 4.5 stars and above with at least 50 completed transactions for
                  the safest shopping experience.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                  Step 4: Compare Prices Across Similar Products
                </h2>
                <p>
                  One of the biggest advantages of the <strong>ACBuy database</strong> is the ability
                  to compare similar products side by side. Do not buy the first listing you see —
                  browse a few options within the same category, compare prices, check shipping
                  estimates, and review buyer photos before making your decision. The extra five
                  minutes of research often saves 10 to 20 dollars per item.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                  Step 5: Understand Shipping Timeframes
                </h2>
                <p>
                  Shipping times in the <strong>ACBuy spreadsheet</strong> vary by product category,
                  seller location, and your destination country. Lightweight items like t-shirts and
                  accessories typically arrive in 5 to 8 days, while heavier items like jackets and
                  bags may take 8 to 14 days. Always check the estimated delivery timeframe before
                  ordering — it is displayed prominently in every ACBuy item list entry.
                </p>

                <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 my-8">
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                    Quick Shopping Tips
                  </h3>
                  <ul className="space-y-2.5">
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </span>
                      <span className="text-sm">Start with smaller, lighter items for your first order to test the process</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </span>
                      <span className="text-sm">Always measure your existing clothing and compare to size charts provided in listings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </span>
                      <span className="text-sm">Use the Sets category for bundled savings of 15 to 25 percent on coordinated outfits</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </span>
                      <span className="text-sm">Check back daily — the ACBuy spreadsheet refreshes every 24 hours with new deals</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </span>
                      <span className="text-sm">Combine orders from the same seller to save on consolidated shipping</span>
                    </li>
                  </ul>
                </div>

                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                  Need More Help?
                </h2>
                <p>
                  If you have questions about using the <strong>ACBuy spreadsheet</strong>, check out
                  our detailed article guides covering each product category in depth. From shoes to
                  accessories, every guide includes specific tips, price comparisons, and shopping
                  strategies tailored to that category.
                </p>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}